import { useEffect, useState } from "react"

interface FontConfig {
  fontFamily: string
  cssPath: string
  className: string
}

type FontManagerConfig = Record<string, FontConfig>

class FontManager {
  private static instances = new Map<string, FontManager>()
  private static globalLoadedFonts = new Set<string>()
  private static globalLoadingPromises = new Map<string, Promise<void>>()

  private fontMap: Record<string, FontConfig>
  private defaultFont: FontConfig | null = null

  private constructor(config: FontManagerConfig) {
    this.fontMap = config
    this.defaultFont = this.fontMap.default ?? null
  }

  static getInstance(appName: string, config?: FontManagerConfig): FontManager {
    if (!this.instances.has(appName)) {
      if (!config) {
        throw new Error(
          `FontManager instance for "${appName}" not found. Please provide configuration.`
        )
      }
      this.instances.set(appName, new FontManager(config))
    }
    const instance = this.instances.get(appName)
    if (!instance) {
      throw new Error(
        `FontManager instance for "${appName}" could not be retrieved.`
      )
    }
    return instance
  }

  async loadFonts(fontFamilies: string[]): Promise<void> {
    const fontConfigs = this.processFontFamilies(fontFamilies)
    const promises = fontConfigs
      .filter((config) => !FontManager.globalLoadedFonts.has(config.cssPath))
      .map((config) => this.loadSingleFont(config))

    await Promise.all(promises)
  }

  private processFontFamilies(fontFamilies: string[]): FontConfig[] {
    if (!fontFamilies || fontFamilies.length === 0) {
      return this.defaultFont ? [this.defaultFont] : []
    }

    const config: FontConfig[] = []
    const loadedFontFamilies = new Set<string>()

    for (const fontFamily of fontFamilies) {
      const fontFamilyOptions = fontFamily.split(",").map((name) => name.trim())

      for (const family of fontFamilyOptions) {
        if (!loadedFontFamilies.has(family)) {
          const foundConfig = this.getFontConfigByName(family)
          if (foundConfig) {
            config.push(foundConfig)
            loadedFontFamilies.add(family)
          }
        }
      }
    }
    return config
  }

  private getFontConfigByName(fontFamily: string): FontConfig | null {
    if (this.fontMap[fontFamily]) {
      return this.fontMap[fontFamily]
    }

    const lowerFontFamily = fontFamily.toLowerCase()
    for (const [key, config] of Object.entries(this.fontMap)) {
      if (key.toLowerCase() === lowerFontFamily) {
        return config
      }
    }

    return this.defaultFont
  }

  private loadSingleFont(fontConfig: FontConfig): Promise<void> {
    if (FontManager.globalLoadingPromises.has(fontConfig.cssPath)) {
      const promise = FontManager.globalLoadingPromises.get(fontConfig.cssPath)
      if (!promise) {
        throw new Error(
          `Promise for font "${fontConfig.cssPath}" could not be retrieved.`
        )
      }
      return promise
    }

    const promise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(
        `link[href="${fontConfig.cssPath}"]`
      )
      if (existing) {
        FontManager.globalLoadedFonts.add(fontConfig.cssPath)
        resolve()
        return
      }

      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = fontConfig.cssPath
      link.onload = () => {
        FontManager.globalLoadedFonts.add(fontConfig.cssPath)
        FontManager.globalLoadingPromises.delete(fontConfig.cssPath)
        resolve()
      }
      link.onerror = () => {
        FontManager.globalLoadingPromises.delete(fontConfig.cssPath)
        reject(new Error(`Failed to load font: ${fontConfig.cssPath}`))
      }
      document.head.appendChild(link)
    })

    FontManager.globalLoadingPromises.set(fontConfig.cssPath, promise)
    return promise
  }

  getFontConfigs(fontFamilies: string[]): FontConfig[] {
    return this.processFontFamilies(fontFamilies)
  }

  static isLoaded(cssPath: string): boolean {
    return this.globalLoadedFonts.has(cssPath)
  }

  static reset(): void {
    this.globalLoadedFonts.clear()
    this.globalLoadingPromises.clear()
    this.instances.clear()
  }
}

export function createFontLoader(appName: string, config: FontManagerConfig) {
  const fontManager = FontManager.getInstance(appName, config)

  return function useFontLoader(fontFamily: string[]) {
    const [fontsLoaded, setFontsLoaded] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
      fontManager
        .loadFonts(fontFamily)
        .then(() => setFontsLoaded(true))
        .catch(setError)
    }, [fontFamily])

    return { fontsLoaded, error }
  }
}

export { FontManager, type FontConfig, type FontManagerConfig }
